import React from 'react';
import { format, parse } from 'date-fns';
import { BLACK_300, CHART_SERIES_PALETTE, MEDIUM_BLUE } from 'constants/colors';
import ReactECharts from 'components/ThemedECharts';
import { displayValue } from 'components/Flux/GeneralTable';
import { useSelector } from 'react-redux';
import { selectFluxAutomaticControlPanelMltOption } from 'redux/reducers/fluxAutomaticControlPanelReducer';
import { Rodadas, dailySeriesName } from 'helper/flux/automatic';
import buildScrollableAxisTooltip from 'helper/chartTooltip';
import { cssVar } from 'constants/theme';

export interface GraphProps {
  data: {
    [key: string]: (string | number)[][],
  },
  formatString?: string,
  rodadas?: Rodadas,
  height?: string,
}

export default function Graph({
  data, formatString = 'dd/MM/yyyy', rodadas = undefined, height,
}: GraphProps) {
  const refDate = new Date();
  const series = Object.keys(data).map((name) => ({
    // "NOME (dd/mm)" quando o BE novo envia rodadas; nome puro no fallback.
    name: dailySeriesName(name, rodadas),
    data: data[name].map((dataPoint) => ({
      name: dataPoint[0],
      value: [format(parse(dataPoint[0] as string, formatString, refDate), 'yyyy-MM-dd'), dataPoint[1]],
    })),
    type: 'line',
    smooth: true,
  }));
  const chartSplitNumber = series[0].data.length;

  const unidade = useSelector(selectFluxAutomaticControlPanelMltOption);
  const tooltipValueFormatter = (v: number) => displayValue(v, unidade === '%mlt');

  const options = {
    // Paleta explícita (mesmos hex da default do ECharts 6): cor por ÍNDICE na
    // ordem de Object.keys(data); o RoundInfoPopover indexa a MESMA constante.
    color: CHART_SERIES_PALETTE,
    // Arranjo como no Windx (plot → legend → slider na base do canvas).
    legend: {
      backgroundColor: '#fff', // tema: cor de dado
      bottom: 28,
      shadowColor: 'rgba(0, 0, 0, 0.1)', // tema: cor de dado (o ThemedECharts troca o fundo; a sombra some no escuro)
      shadowBlur: 10,
      type: 'scroll',
    },
    grid: {
      bottom: 96,
    },
    xAxis: {
      type: 'time',
      axisLabel: {
        formatter: {
          month: '{MMMM}',
        },
        showMaxLabel: true,
      },
      splitNumber: chartSplitNumber,
    },
    yAxis: {
      type: 'value',
      min: 0.0,
      axisLabel: {
        formatter: (v: number) => (unidade === '%mlt' ? `${(v * 100).toFixed(0)}%` : v),
      },
    },
    series,
    // Scroll + filtro de séries sem valor no ponto (N/A), como no Windx.
    tooltip: buildScrollableAxisTooltip(tooltipValueFormatter),
    // A régua de zoom do eixo X (embaixo do gráfico) fica em todos os modos — é o controle que
    // o usuário usa pra medir/navegar o período. Já a régua do eixo Y só existia no desktop: no
    // mobile ela reservava espaço demais num gráfico já apertado, então some quando `height` é
    // setado (card inline ou tela cheia).
    dataZoom: [
      {
        type: 'slider',
        show: 'true',
        xAxisIndex: [0],
        start: 0,
        end: 100,
        // Abaixo da legend (bottom: 28), rente à base — padrão Windx.
        bottom: 6,
        borderRadius: 32,
        borderColor: BLACK_300, // tema: cor de dado
        height: 9,
        fillerColor: MEDIUM_BLUE, // tema: cor de dado
        showDetail: false,
        showDataShadow: false,
        handleIcon: 'path://m50,50 a100,100 0 1,0 -200,0 a 100,100 0 1,0 200,0 Z',
        handleSize: 14,
        handleStyle: {
          borderColor: MEDIUM_BLUE, // tema: cor de dado
          borderWidth: 2,
        },
        moveHandleStyle: {
          opacity: 0,
        },
        brushSelect: false,
        brushStyle: {
          opacity: 0,
        },
        dataBackground: {
          lineStyle: {
            opacity: 0,
          },
          areaStyle: {
            opacity: 0,
          },
        },
      },
      ...(height ? [] : [{
        type: 'slider',
        show: 'true',
        yAxisIndex: [0],
        start: 0,
        end: 100,
        filterMode: 'none',
        borderRadius: 32,
        borderColor: BLACK_300, // tema: cor de dado
        fillerColor: MEDIUM_BLUE, // tema: cor de dado
        width: 9,
        showDetail: false,
        showDataShadow: false,
        handleIcon: 'path://m50,50 a100,100 0 1,0 -200,0 a 100,100 0 1,0 200,0 Z',
        handleSize: 14,
        handleStyle: {
          borderColor: MEDIUM_BLUE, // tema: cor de dado
          borderWidth: 2,
        },
        moveHandleStyle: {
          opacity: 0,
        },
        brushSelect: false,
        brushStyle: {
          opacity: 0,
        },
        dataBackground: {
          lineStyle: {
            opacity: 0,
          },
          areaStyle: {
            opacity: 0,
          },
        },
      }]),
    ],
  };

  // Com `height` setado (mobile), o valor tem que se propagar em `height: '100%'` por cada nível
  // até o canvas do ECharts — uma div sem altura própria no meio da cadeia faz o `height: '100%'`
  // do ECharts colapsar (percentual sem base definida), deixando o gráfico minúsculo.
  return (
    <div className="my-3" style={height ? { backgroundColor: cssVar('bg-surface'), height, width: '100%' } : { backgroundColor: cssVar('bg-surface') }}>
      <ReactECharts
        option={options}
        style={height ? { width: '100%', height: '100%' } : {
          width: '100%',
          height: 'auto',
          aspectRatio: '16 / 9',
        }}
      />
    </div>
  );
}
